import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarSquareIcon,
  CircleStackIcon,
  CheckCircleIcon,
  XCircleIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ClockIcon,
  ServerStackIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  CpuChipIcon,
  CloudIcon,
  CodeBracketIcon,
  ClipboardDocumentIcon,
  TrashIcon,
  PencilIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { TIMEZONES, TIMEZONE_GROUPS } from '../constants/timezones';

function CreateCompanyModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    timezone: 'UTC',
    dataLocation: '',
    billingLimits: {
      maxSeats: 1,
      maxStorageGB: 0.5
    },
    credits: {
      monthly: 0,
      permanent: 0
    }
  });
  
  // Fetch available regions when modal opens
  useEffect(() => {
    if (isOpen) {
      loadRegions();
    }
  }, [isOpen]);
  
  const loadRegions = async () => {
    try {
      setLoadingRegions(true);
      const response = await adminApi.getAvailableRegions();
      const availableRegions = response.data.regions || [];
      setRegions(availableRegions);
      
      // Set default region if available
      const defaultRegion = availableRegions.find(r => r.isDefault);
      if (defaultRegion && !formData.dataLocation) {
        setFormData(p => ({ ...p, dataLocation: defaultRegion.regionId }));
      } else if (availableRegions.length > 0 && !formData.dataLocation) {
        setFormData(p => ({ ...p, dataLocation: availableRegions[0].regionId }));
      }
    } catch (error) {
      console.error('Failed to load regions:', error);
    } finally {
      setLoadingRegions(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await adminApi.createCompany(formData);
      toast.success('Company created successfully!');
      onSuccess();
      onClose();
      setFormData({
        name: '',
        industry: '',
        timezone: 'UTC',
        dataLocation: '',
        billingLimits: { maxSeats: 1, maxStorageGB: 0.5 },
        credits: { monthly: 0, permanent: 0 }
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Create New Company
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                className="input"
                placeholder="Acme Corporation"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Industry
              </label>
              <select
                value={formData.industry}
                onChange={(e) => setFormData(p => ({ ...p, industry: e.target.value }))}
                className="input"
              >
                <option value="">Select Industry</option>
                <option value="technology">Technology</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Finance</option>
                <option value="retail">Retail</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="education">Education</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            {/* Timezone */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                <ClockIcon className="w-4 h-4 inline mr-1" />
                Timezone
              </label>
              <select
                value={formData.timezone}
                onChange={(e) => setFormData(p => ({ ...p, timezone: e.target.value }))}
                className="input"
                required
              >
                {TIMEZONE_GROUPS.map(group => (
                  <optgroup key={group} label={group}>
                    {TIMEZONES.filter(tz => tz.group === group).map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-surface-400 mt-1">
                This timezone will be used for AI responses and date/time formatting
              </p>
            </div>
            
            {/* Data Warehouse Region */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                <GlobeAltIcon className="w-4 h-4 inline mr-1" />
                Data Warehouse Region *
              </label>
              {loadingRegions ? (
                <div className="input flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : regions.length === 0 ? (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-400">
                  <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
                  No regions configured. Please add regions in the Regions page first.
                </div>
              ) : (
                <>
                  <select
                    value={formData.dataLocation}
                    onChange={(e) => setFormData(p => ({ ...p, dataLocation: e.target.value }))}
                    className="input"
                    required
                  >
                    {regions.map((region) => (
                      <option key={region.regionId} value={region.regionId}>
                        {region.flag} {region.name} ({region.regionId})
                        {region.isDefault ? ' - Default' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-surface-400 mt-1">
                    <ExclamationTriangleIcon className="w-3 h-3 inline mr-1" />
                    Region cannot be changed after company creation
                  </p>
                </>
              )}
            </div>
            
            <div className="border-t border-surface-700 pt-4">
              <h4 className="text-sm font-medium text-surface-300 mb-3">Billing Limits</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-surface-400 mb-1">Max Seats</label>
                  <input
                    type="number"
                    value={formData.billingLimits.maxSeats}
                    onChange={(e) => setFormData(p => ({
                      ...p,
                      billingLimits: { ...p.billingLimits, maxSeats: parseInt(e.target.value) || 1 }
                    }))}
                    className="input"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-surface-400 mb-1">Storage (GB)</label>
                  <input
                    type="number"
                    value={formData.billingLimits.maxStorageGB}
                    onChange={(e) => setFormData(p => ({
                      ...p,
                      billingLimits: { ...p.billingLimits, maxStorageGB: parseFloat(e.target.value) || 0.5 }
                    }))}
                    className="input"
                    min="0.1"
                    step="0.1"
                  />
                  <p className="text-xs text-surface-500 mt-1">e.g., 0.5 = 500 MB</p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-surface-700 pt-4">
              <h4 className="text-sm font-medium text-surface-300 mb-3">
                <SparklesIcon className="w-4 h-4 inline mr-1" />
                Initial Credits
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-surface-400 mb-1">
                    <ClockIcon className="w-3 h-3 inline mr-1" />
                    Monthly Credits
                  </label>
                  <input
                    type="number"
                    value={formData.credits.monthly}
                    onChange={(e) => setFormData(p => ({
                      ...p,
                      credits: { ...p.credits, monthly: parseInt(e.target.value) || 0 }
                    }))}
                    className="input"
                    min="0"
                    step="100"
                  />
                  <p className="text-xs text-surface-500 mt-1">Refresh each billing cycle</p>
                </div>
                <div>
                  <label className="block text-xs text-surface-400 mb-1">
                    <SparklesIcon className="w-3 h-3 inline mr-1" />
                    Permanent Credits
                  </label>
                  <input
                    type="number"
                    value={formData.credits.permanent}
                    onChange={(e) => setFormData(p => ({
                      ...p,
                      credits: { ...p.credits, permanent: parseInt(e.target.value) || 0 }
                    }))}
                    className="input"
                    min="0"
                    step="100"
                  />
                  <p className="text-xs text-surface-500 mt-1">Never expire</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function CompanyDetailsModal({ isOpen, onClose, company: initialCompany, onSuccess }) {
  const [company, setCompany] = useState(initialCompany);
  const [loading, setLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [postpaidReport, setPostpaidReport] = useState(null);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  
  // Cost analysis state
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [loadingCosts, setLoadingCosts] = useState(false);
  const [costPeriod, setCostPeriod] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  
  // SDK state
  const [sdkConfig, setSdkConfig] = useState(null);
  const [loadingSDK, setLoadingSDK] = useState(false);
  const [sdkForm, setSdkForm] = useState({
    enabled: false,
    allowedFilters: [],
    customPrompt: '',
    widgetConfig: {
      primaryColor: '#6366f1',
      title: 'AI Assistant',
      placeholder: 'Ask me anything about your data...',
      welcomeMessage: 'Hello! How can I help you today?'
    }
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [editingFilter, setEditingFilter] = useState(null);
  const [filterForm, setFilterForm] = useState({
    fieldName: '',
    displayName: '',
    description: '',
    dataType: 'string',
    required: false
  });
  
  // Update local company state when prop changes
  useEffect(() => {
    setCompany(initialCompany);
  }, [initialCompany]);

  // Credit adjustment form
  const [creditForm, setCreditForm] = useState({
    amount: 0,
    creditType: 'permanent', // 'monthly' or 'permanent'
    description: ''
  });
  
  // Limits adjustment form
  const [limitsForm, setLimitsForm] = useState({
    maxSeats: company?.billingLimits?.maxSeats || 5,
    maxStorageGB: company?.billingLimits?.maxStorageGB || 10,
    additionalSeats: company?.additionalSeats || 0,
    giftedSeats: company?.giftedSeats || 0,
    customStorageGB: company?.customStorageGB || 0,
    customSharedDashboards: company?.customSharedDashboards ?? ''
  });
  
  // Billing plan form
  const [billingForm, setBillingForm] = useState({
    billingPlan: company?.billingPlan || 'starter',
    billingType: company?.billingType || 'prepaid',
    postpaidSettings: {
      creditRate: company?.postpaidSettings?.creditRate || 0,
      invoiceEmail: company?.postpaidSettings?.invoiceEmail || '',
      billingContact: company?.postpaidSettings?.billingContact || '',
      // Monthly charges
      licenseFee: company?.postpaidSettings?.licenseFee || 0,
      storageCostPerGB: company?.postpaidSettings?.storageCostPerGB || 0,
      sdkFee: company?.postpaidSettings?.sdkFee || 0,
      additionalCharges: company?.postpaidSettings?.additionalCharges || [],
      // Payment method
      paymentMethod: company?.postpaidSettings?.paymentMethod || 'bank_transfer',
      // Currency
      currencyId: company?.postpaidSettings?.currencyId || '',
      currencyCode: company?.postpaidSettings?.currencyCode || 'IDR',
      currencySymbol: company?.postpaidSettings?.currencySymbol || 'Rp'
    }
  });
  
  useEffect(() => {
    if (isOpen && company) {
      loadCreditBalance();
      loadSubscriptionPlans();
      loadCurrencies();
      
      // Update forms when company changes
      setLimitsForm({
        maxSeats: company.billingLimits?.maxSeats || 5,
        maxStorageGB: company.billingLimits?.maxStorageGB || 10,
        additionalSeats: company.additionalSeats || 0,
        giftedSeats: company.giftedSeats || 0,
        customStorageGB: company.customStorageGB || 0,
        customSharedDashboards: company.customSharedDashboards ?? ''
      });
      
      setBillingForm({
        billingPlan: company.billingPlan || 'starter',
        billingType: company.billingType || 'prepaid',
        postpaidSettings: {
          creditRate: company.postpaidSettings?.creditRate || 0,
          invoiceEmail: company.postpaidSettings?.invoiceEmail || '',
          billingContact: company.postpaidSettings?.billingContact || '',
          licenseFee: company.postpaidSettings?.licenseFee || 0,
          storageCostPerGB: company.postpaidSettings?.storageCostPerGB || 0,
          sdkFee: company.postpaidSettings?.sdkFee || 0,
          additionalCharges: company.postpaidSettings?.additionalCharges || [],
          paymentMethod: company.postpaidSettings?.paymentMethod || 'bank_transfer',
          currencyId: company.postpaidSettings?.currencyId || '',
          currencyCode: company.postpaidSettings?.currencyCode || 'IDR',
          currencySymbol: company.postpaidSettings?.currencySymbol || 'Rp'
        }
      });
      
      // Load SDK config if enterprise
      if (company.billingPlan === 'enterprise') {
        loadSDKConfig();
      }
      
      // Load postpaid report if company is postpaid
      if (company.billingPlan === 'enterprise' && company.billingType === 'postpaid') {
        loadPostpaidReport();
      }
    }
  }, [isOpen, company]);
  
  // Load cost analysis when switching to costs tab
  useEffect(() => {
    if (isOpen && company && activeTab === 'costs') {
      loadCostAnalysis(costPeriod.month, costPeriod.year);
    }
  }, [isOpen, company, activeTab, costPeriod]);
  
  const loadCreditBalance = async () => {
    try {
      setLoadingCredits(true);
      const response = await adminApi.getCompanyCredits(company._id);
      setCreditBalance(response.data);
    } catch (error) {
      console.error('Failed to load credits:', error);
      toast.error('Failed to load credit balance');
    } finally {
      setLoadingCredits(false);
    }
  };
  
  const loadSubscriptionPlans = async () => {
    try {
      const response = await adminApi.getSubscriptionPlans();
      setSubscriptionPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  };
  
  const loadCurrencies = async () => {
    try {
      const response = await adminApi.getCurrencies();
      setCurrencies(response.data.currencies || []);
    } catch (error) {
      console.error('Failed to load currencies:', error);
    }
  };
  
  const loadPostpaidReport = async () => {
    try {
      const response = await adminApi.getPostpaidReport(company._id, {});
      setPostpaidReport(response.data.report);
    } catch (error) {
      console.error('Failed to load postpaid report:', error);
    }
  };
  
  const loadCostAnalysis = async (month, year) => {
    try {
      setLoadingCosts(true);
      const response = await adminApi.getCompanyCostAnalysis(company._id, {
        month: month - 1, // API expects 0-indexed month
        year
      });
      setCostAnalysis(response.data);
    } catch (error) {
      console.error('Failed to load cost analysis:', error);
      toast.error('Failed to load cost analysis');
    } finally {
      setLoadingCosts(false);
    }
  };

  const reloadCompanyData = async () => {
    try {
      const response = await adminApi.getCompany(company._id);
      setCompany(response.data);
    } catch (error) {
      console.error('Failed to reload company data:', error);
    }
  };
  
  // SDK Functions
  const loadSDKConfig = async () => {
    try {
      setLoadingSDK(true);
      const response = await adminApi.getSDKConfig(company._id);
      setSdkConfig(response.data);
      setSdkForm({
        enabled: response.data.enabled || false,
        allowedFilters: response.data.allowedFilters || [],
        customPrompt: response.data.customPrompt || '',
        widgetConfig: response.data.widgetConfig || {
          primaryColor: '#6366f1',
          title: 'AI Assistant',
          placeholder: 'Ask me anything about your data...',
          welcomeMessage: 'Hello! How can I help you today?'
        }
      });
    } catch (error) {
      console.error('Failed to load SDK config:', error);
    } finally {
      setLoadingSDK(false);
    }
  };
  
  const handleUpdateSDKConfig = async () => {
    setLoading(true);
    try {
      const response = await adminApi.updateSDKConfig(company._id, sdkForm);
      setSdkConfig(response.data);
      toast.success('SDK configuration updated');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update SDK configuration');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegenerateApiKey = async () => {
    if (!window.confirm('Are you sure you want to regenerate the API key? The old key will be invalidated immediately.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await adminApi.regenerateSDKApiKey(company._id);
      setNewApiKey(response.data.apiKey);
      setSdkConfig(prev => ({
        ...prev,
        hasApiKey: true,
        apiKeyLast4: response.data.apiKeyLast4,
        apiKeyCreatedAt: response.data.apiKeyCreatedAt
      }));
      toast.success('New API key generated! Make sure to copy it now - it won\'t be shown again.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to regenerate API key');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddFilter = () => {
    if (!filterForm.fieldName || !filterForm.displayName) {
      toast.error('Field name and display name are required');
      return;
    }
    
    // Check for duplicate field names
    if (sdkForm.allowedFilters.some(f => f.fieldName === filterForm.fieldName)) {
      toast.error('A filter with this field name already exists');
      return;
    }
    
    setSdkForm(prev => ({
      ...prev,
      allowedFilters: [...prev.allowedFilters, { ...filterForm }]
    }));
    
    setFilterForm({
      fieldName: '',
      displayName: '',
      description: '',
      dataType: 'string',
      required: false
    });
    setEditingFilter(null);
  };
  
  const handleEditFilter = (index) => {
    setFilterForm(sdkForm.allowedFilters[index]);
    setEditingFilter(index);
  };
  
  const handleUpdateFilter = () => {
    if (!filterForm.fieldName || !filterForm.displayName) {
      toast.error('Field name and display name are required');
      return;
    }
    
    setSdkForm(prev => ({
      ...prev,
      allowedFilters: prev.allowedFilters.map((f, i) => 
        i === editingFilter ? { ...filterForm } : f
      )
    }));
    
    setFilterForm({
      fieldName: '',
      displayName: '',
      description: '',
      dataType: 'string',
      required: false
    });
    setEditingFilter(null);
  };
  
  const handleDeleteFilter = (index) => {
    setSdkForm(prev => ({
      ...prev,
      allowedFilters: prev.allowedFilters.filter((_, i) => i !== index)
    }));
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  
  const handleAdjustCredits = async (e) => {
    e.preventDefault();
    
    if (!creditForm.amount || creditForm.amount === 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!creditForm.description || creditForm.description.trim() === '') {
      toast.error('Please enter a reason for the adjustment');
      return;
    }
    
    setLoading(true);
    try {
      await adminApi.adjustCompanyCredits(company._id, {
        amount: parseInt(creditForm.amount),
        creditType: creditForm.creditType,
        description: creditForm.description
      });
      
      toast.success(`Successfully ${creditForm.amount > 0 ? 'added' : 'deducted'} ${Math.abs(creditForm.amount)} ${creditForm.creditType} credits`);
      
      // Reset form
      setCreditForm({ amount: 0, creditType: 'permanent', description: '' });
      
      // Reload credit balance
      loadCreditBalance();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to adjust credits');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateLimits = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const updateData = {
        billingLimits: {
          maxSeats: parseInt(limitsForm.maxSeats),
          maxStorageGB: parseFloat(limitsForm.maxStorageGB),
          maxAITokensPerMonth: company.billingLimits?.maxAITokensPerMonth || 100000
        },
        additionalSeats: parseInt(limitsForm.additionalSeats),
        giftedSeats: parseInt(limitsForm.giftedSeats || 0),
        customStorageGB: parseFloat(limitsForm.customStorageGB || 0)
      };
      if (company.billingPlan === 'enterprise') {
        updateData.customSharedDashboards = limitsForm.customSharedDashboards === '' || limitsForm.customSharedDashboards === null
          ? undefined
          : parseInt(limitsForm.customSharedDashboards) || undefined;
      }
      await adminApi.updateCompany(company._id, updateData);
      
      toast.success('Company limits updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update limits');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateBillingPlan = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const updateData = {
        billingPlan: billingForm.billingPlan,
        billingType: billingForm.billingType
      };
      
      // Only include postpaid settings if enterprise and postpaid
      if (billingForm.billingPlan === 'enterprise' && billingForm.billingType === 'postpaid') {
        updateData.postpaidSettings = {
          creditRate: parseFloat(billingForm.postpaidSettings.creditRate) || 0,
          invoiceEmail: billingForm.postpaidSettings.invoiceEmail,
          billingContact: billingForm.postpaidSettings.billingContact,
          licenseFee: parseFloat(billingForm.postpaidSettings.licenseFee) || 0,
          storageCostPerGB: parseFloat(billingForm.postpaidSettings.storageCostPerGB) || 0,
          sdkFee: parseFloat(billingForm.postpaidSettings.sdkFee) || 0,
          additionalCharges: billingForm.postpaidSettings.additionalCharges || [],
          paymentMethod: billingForm.postpaidSettings.paymentMethod || 'bank_transfer',
          currencyId: billingForm.postpaidSettings.currencyId || null,
          currencyCode: billingForm.postpaidSettings.currencyCode || 'IDR',
          currencySymbol: billingForm.postpaidSettings.currencySymbol || 'Rp'
        };
      }
      
      await adminApi.updateCompany(company._id, updateData);
      
      toast.success('Billing plan updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update billing plan');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGeneratePaymentLink = async () => {
    setGeneratingPaymentLink(true);
    
    try {
      // Calculate total invoice amount
      const licenseFee = parseFloat(billingForm.postpaidSettings.licenseFee) || 0;
      const storageGB = Math.max(1, Math.ceil(company.currentUsage?.storageUsedGB || 0));
      const storageCost = storageGB * (parseFloat(billingForm.postpaidSettings.storageCostPerGB) || 0);
      const creditsCost = (postpaidReport?.totalCredits || 0) * (parseFloat(billingForm.postpaidSettings.creditRate) || 0);
      const totalAmount = licenseFee + storageCost + creditsCost;
      
      if (totalAmount <= 0) {
        toast.error('Invoice amount must be greater than zero');
        return;
      }
      
      // Get current billing period
      const now = new Date();
      const billingPeriod = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      // Prepare line items for the invoice
      const lineItems = [
        { description: 'License Fee', amount: licenseFee },
        { description: `Storage (${storageGB} GB)`, amount: storageCost },
        { description: `Credits Used (${postpaidReport?.totalCredits || 0})`, amount: creditsCost }
      ];
      
      const response = await adminApi.createEnterprisePaymentLink({
        companyId: company._id,
        amount: totalAmount,
        currencyCode: billingForm.postpaidSettings.currencyCode || 'IDR',
        description: `Invoice - ${company.name}`,
        billingPeriod,
        lineItems
      });
      
      // Reload company data to get the saved payment link
      await reloadCompanyData();
      
      // Also trigger parent refresh
      if (onSuccess) {
        onSuccess();
      }
      
      // Show success message with payment link
      const paymentUrl = response.data.paymentLink;
      toast.success(
        <div>
          <p className="font-semibold mb-2">Payment link created!</p>
          <a 
            href={paymentUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-300 hover:text-blue-200 underline break-all"
            onClick={(e) => e.stopPropagation()}
          >
            Click to open payment link
          </a>
        </div>,
        { duration: 10000 }
      );
      
      // Copy link to clipboard
      navigator.clipboard.writeText(paymentUrl);
      toast.success('Payment link copied to clipboard!', { duration: 3000 });
      
    } catch (error) {
      console.error('Generate payment link error:', error);
      toast.error(error.response?.data?.error || 'Failed to generate payment link');
    } finally {
      setGeneratingPaymentLink(false);
    }
  };
  
  if (!company) return null;
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6 sticky top-0 bg-surface-800 pb-4 border-b border-surface-700">
            <div>
              <Dialog.Title className="text-xl font-semibold text-surface-100">
                {company.name}
              </Dialog.Title>
              <p className="text-sm text-surface-400 mt-1">
                {company.industry || 'No industry set'} • {company.billingPlan || 'No plan'}
              </p>
            </div>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-surface-700 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-surface-400 hover:text-surface-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'billing'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-surface-400 hover:text-surface-300'
              }`}
            >
              <CreditCardIcon className="w-4 h-4 inline mr-1" />
              Billing Plan
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'credits'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-surface-400 hover:text-surface-300'
              }`}
            >
              <SparklesIcon className="w-4 h-4 inline mr-1" />
              Credits
            </button>
            <button
              onClick={() => setActiveTab('limits')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'limits'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-surface-400 hover:text-surface-300'
              }`}
            >
              <ServerStackIcon className="w-4 h-4 inline mr-1" />
              Limits
            </button>
            <button
              onClick={() => setActiveTab('costs')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'costs'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-surface-400 hover:text-surface-300'
              }`}
            >
              <CurrencyDollarIcon className="w-4 h-4 inline mr-1" />
              Costs
            </button>
            {/* SDK Tab - Only for Enterprise */}
            {billingForm.billingPlan === 'enterprise' && (
              <button
                onClick={() => setActiveTab('sdk')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'sdk'
                    ? 'border-primary-500 text-primary-400'
                    : 'border-transparent text-surface-400 hover:text-surface-300'
                }`}
              >
                <CodeBracketIcon className="w-4 h-4 inline mr-1" />
                SDK
              </button>
            )}
          </div>
          
          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-surface-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <UsersIcon className="w-5 h-5 text-primary-400" />
                    <span className="text-sm text-surface-400">Users / Seats</span>
                  </div>
                  <p className="text-2xl font-bold text-surface-100">
                    {company.currentUsage?.seats || 0} / {(company.billingLimits?.maxSeats || 1) + (company.additionalSeats || 0) + (company.giftedSeats || 0)}
                  </p>
                  <div className="text-xs text-surface-500 mt-1 space-y-0.5">
                    <div>Base: {company.billingLimits?.maxSeats || 1}</div>
                    {(company.additionalSeats || 0) > 0 && (
                      <div className="text-yellow-500/70">+ {company.additionalSeats} paid</div>
                    )}
                    {(company.giftedSeats || 0) > 0 && (
                      <div className="text-green-500/70">+ {company.giftedSeats} gifted 🎁</div>
                    )}
                  </div>
                </div>
                
                <div className="bg-surface-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CircleStackIcon className="w-5 h-5 text-blue-400" />
                    <span className="text-sm text-surface-400">Storage</span>
                  </div>
                  <p className="text-2xl font-bold text-surface-100">
                    {company.currentUsage?.storageUsedGB?.toFixed(1) || 0} GB
                  </p>
                  <p className="text-xs text-surface-500 mt-1">
                    of {company.billingLimits?.maxStorageGB || 10} GB
                  </p>
                </div>
                
                <div className="bg-surface-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ChartBarSquareIcon className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm text-surface-400">Dashboards</span>
                  </div>
                  <p className="text-2xl font-bold text-surface-100">{company.dashboardCount || 0}</p>
                </div>
                
                <div className="bg-surface-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <GlobeAltIcon className="w-5 h-5 text-purple-400" />
                    <span className="text-sm text-surface-400">Region</span>
                  </div>
                  <p className="text-lg font-semibold text-surface-100">
                    {company.dataLocation || 'N/A'}
                  </p>
                </div>
              </div>
              
              <div className="border-t border-surface-700 pt-4">
                <h4 className="text-sm font-medium text-surface-300 mb-3">Company Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-surface-400">Status:</span>
                    <span className={`ml-2 ${company.isActive ? 'text-emerald-400' : 'text-red-400'}`}>
                      {company.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <span className="text-surface-400">Billing Plan:</span>
                    <span className="ml-2 text-surface-200">{company.billingPlan || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-surface-400">Company ID:</span>
                    <span className="ml-2 text-surface-300 font-mono text-xs">{company._id}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'billing' && (
            <div className="space-y-6">
              <form onSubmit={handleUpdateBillingPlan} className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-surface-300 mb-4">Subscription Plan</h4>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Select Plan
                    </label>
                    <select
                      value={billingForm.billingPlan}
                      onChange={(e) => setBillingForm(p => ({ ...p, billingPlan: e.target.value }))}
                      className="input"
                      required
                    >
                      {subscriptionPlans.map(plan => (
                        <option key={plan.slug} value={plan.slug}>
                          {plan.name} {plan.isContactSales ? '(Contact Sales)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-surface-500 mt-2">
                      Current plan: <span className="text-surface-300 font-medium">{company.billingPlan}</span>
                    </p>
                  </div>
                </div>
                
                {/* Enterprise-specific options */}
                {billingForm.billingPlan === 'enterprise' && (
                  <div className="border-t border-surface-700 pt-6">
                    <h4 className="text-sm font-medium text-surface-300 mb-4">Enterprise Billing Settings</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-3">
                          Billing Type
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-3 p-3 border border-surface-700 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                            <input
                              type="radio"
                              value="prepaid"
                              checked={billingForm.billingType === 'prepaid'}
                              onChange={(e) => setBillingForm(p => ({ ...p, billingType: e.target.value }))}
                              className="text-primary-500"
                            />
                            <div>
                              <div className="font-medium text-surface-200">Prepaid (Buy Credits)</div>
                              <div className="text-xs text-surface-400">Company purchases credit packs as needed</div>
                            </div>
                          </label>
                          
                          <label className="flex items-center gap-3 p-3 border border-surface-700 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                            <input
                              type="radio"
                              value="postpaid"
                              checked={billingForm.billingType === 'postpaid'}
                              onChange={(e) => setBillingForm(p => ({ ...p, billingType: e.target.value }))}
                              className="text-primary-500"
                            />
                            <div>
                              <div className="font-medium text-surface-200">Postpaid (Monthly Invoice)</div>
                              <div className="text-xs text-surface-400">Track usage and invoice monthly</div>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      {/* Postpaid Settings */}
                      {billingForm.billingType === 'postpaid' && (
                        <div className="bg-surface-700/30 border border-surface-700 rounded-lg p-4 space-y-4">
                          <h5 className="text-sm font-medium text-surface-200">Postpaid Configuration</h5>
                          
                          {/* Payment Method - Bank Transfer Only */}
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-3">
                              Payment Method
                            </label>
                            <div className="p-3 border border-primary-500 bg-primary-500/10 rounded-lg">
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  value="bank_transfer"
                                  checked={true}
                                  readOnly
                                  className="text-primary-500"
                                />
                                <div>
                                  <div className="font-medium text-surface-200">Bank Transfer</div>
                                  <div className="text-xs text-surface-400">Enterprise invoices paid via direct bank transfer</div>
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-surface-500 mt-2">
                              Invoices will be sent to the customer with your bank account details for manual payment.
                            </p>
                          </div>
                          
                          {/* Contact Information */}
                          <div className="border-t border-surface-600 pt-4">
                            <h6 className="text-sm font-medium text-surface-300 mb-3">Contact Information</h6>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-surface-300 mb-2">
                                  Invoice Email
                                </label>
                                <input
                                  type="email"
                                  value={billingForm.postpaidSettings.invoiceEmail}
                                  onChange={(e) => setBillingForm(p => ({
                                    ...p,
                                    postpaidSettings: { ...p.postpaidSettings, invoiceEmail: e.target.value }
                                  }))}
                                  className="input"
                                  placeholder="billing@company.com"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-surface-300 mb-2">
                                  Billing Contact Name
                                </label>
                                <input
                                  type="text"
                                  value={billingForm.postpaidSettings.billingContact}
                                  onChange={(e) => setBillingForm(p => ({
                                    ...p,
                                    postpaidSettings: { ...p.postpaidSettings, billingContact: e.target.value }
                                  }))}
                                  className="input"
                                  placeholder="John Doe"
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Invoice Currency */}
                          <div className="border-t border-surface-600 pt-4">
                            <h6 className="text-sm font-medium text-surface-300 mb-3">Invoice Currency</h6>
                            <select
                              value={billingForm.postpaidSettings.currencyId || ''}
                              onChange={(e) => {
                                const selected = currencies.find(c => c._id === e.target.value);
                                setBillingForm(p => ({
                                  ...p,
                                  postpaidSettings: {
                                    ...p.postpaidSettings,
                                    currencyId: e.target.value,
                                    currencyCode: selected?.code || 'IDR',
                                    currencySymbol: selected?.symbol || 'Rp'
                                  }
                                }));
                              }}
                              className="input"
                            >
                              <option value="">Select Currency</option>
                              {currencies.map(currency => (
                                <option key={currency._id} value={currency._id}>
                                  {currency.code} - {currency.name} ({currency.symbol})
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-surface-500 mt-1">
                              All invoice amounts will be displayed in this currency
                            </p>
                          </div>
                          
                          {/* Monthly Charges */}
                          <div className="border-t border-surface-600 pt-4">
                            <h6 className="text-sm font-medium text-surface-300 mb-3">
                              Monthly Charges ({billingForm.postpaidSettings.currencyCode || 'IDR'})
                            </h6>
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-surface-300 mb-2">
                                  Credit Rate (per credit)
                                </label>
                                <input
                                  type="number"
                                  value={billingForm.postpaidSettings.creditRate}
                                  onChange={(e) => setBillingForm(p => ({
                                    ...p,
                                    postpaidSettings: { ...p.postpaidSettings, creditRate: e.target.value }
                                  }))}
                                  className="input"
                                  min="0"
                                  step="any"
                                  placeholder="e.g., 0.05 (USD) or 1000 (IDR)"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-surface-300 mb-2">
                                  License Fee (monthly)
                                </label>
                                <input
                                  type="number"
                                  value={billingForm.postpaidSettings.licenseFee}
                                  onChange={(e) => setBillingForm(p => ({
                                    ...p,
                                    postpaidSettings: { ...p.postpaidSettings, licenseFee: e.target.value }
                                  }))}
                                  className="input"
                                  min="0"
                                  step="any"
                                  placeholder="e.g., 100 (USD) or 500000 (IDR)"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-surface-300 mb-2">
                                  Storage Cost (per GB)
                                </label>
                                <input
                                  type="number"
                                  value={billingForm.postpaidSettings.storageCostPerGB}
                                  onChange={(e) => setBillingForm(p => ({
                                    ...p,
                                    postpaidSettings: { ...p.postpaidSettings, storageCostPerGB: e.target.value }
                                  }))}
                                  className="input"
                                  min="0"
                                  step="any"
                                  placeholder="e.g., 0.1 (USD) or 5000 (IDR)"
                                />
                              </div>
                            </div>
                            
                            {/* SDK Fee - only shown when SDK is enabled */}
                            {sdkConfig?.enabled && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-surface-300 mb-2">
                                  <CodeBracketIcon className="w-4 h-4 inline mr-1" />
                                  SDK Fee (monthly)
                                </label>
                                <input
                                  type="number"
                                  value={billingForm.postpaidSettings.sdkFee}
                                  onChange={(e) => setBillingForm(p => ({
                                    ...p,
                                    postpaidSettings: { ...p.postpaidSettings, sdkFee: e.target.value }
                                  }))}
                                  className="input"
                                  min="0"
                                  step="any"
                                  placeholder="e.g., 200000 (IDR)"
                                />
                                <p className="text-xs text-surface-500 mt-1">
                                  Monthly fee for AI Assistant SDK access
                                </p>
                              </div>
                            )}
                            
                            <p className="text-xs text-surface-500 mt-2">
                              These charges will be included in the monthly invoice
                            </p>
                          </div>
                          
                          {/* Current Postpaid Usage & Invoice Estimate */}
                          {company.billingType === 'postpaid' && (
                            <div className="border-t border-surface-600 pt-4 mt-4">
                              <h6 className="text-sm font-medium text-surface-200 mb-3">
                                Invoice Estimate (Current Period) - {billingForm.postpaidSettings.currencyCode || 'IDR'}
                              </h6>
                              
                              {/* Invoice Line Items */}
                              <div className="bg-surface-800/50 rounded-lg p-4 space-y-3">
                                {/* License Fee */}
                                <div className="flex justify-between text-sm">
                                  <span className="text-surface-300">License Fee</span>
                                  <span className="text-surface-200 font-medium">
                                    {billingForm.postpaidSettings.currencySymbol || 'Rp'} {(parseFloat(billingForm.postpaidSettings.licenseFee) || 0).toLocaleString()}
                                  </span>
                                </div>
                                
                                {/* Storage Cost - Round up to nearest GB (minimum 1 GB if any storage used) */}
                                <div className="flex justify-between text-sm">
                                  <span className="text-surface-300">
                                    Storage ({company.currentUsage?.storageUsedGB?.toFixed(2) || 0} GB → {Math.max(1, Math.ceil(company.currentUsage?.storageUsedGB || 0))} GB × {billingForm.postpaidSettings.currencySymbol || 'Rp'} {(parseFloat(billingForm.postpaidSettings.storageCostPerGB) || 0).toLocaleString()})
                                  </span>
                                  <span className="text-surface-200 font-medium">
                                    {billingForm.postpaidSettings.currencySymbol || 'Rp'} {(Math.max(1, Math.ceil(company.currentUsage?.storageUsedGB || 0)) * (parseFloat(billingForm.postpaidSettings.storageCostPerGB) || 0)).toLocaleString()}
                                  </span>
                                </div>
                                
                                {/* Credit Usage */}
                                {postpaidReport && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-surface-300">
                                      Credits Used ({postpaidReport.totalCredits?.toLocaleString() || 0} × {billingForm.postpaidSettings.currencySymbol || 'Rp'} {(parseFloat(billingForm.postpaidSettings.creditRate) || 0).toLocaleString()})
                                    </span>
                                    <span className="text-surface-200 font-medium">
                                      {billingForm.postpaidSettings.currencySymbol || 'Rp'} {((postpaidReport.totalCredits || 0) * (parseFloat(billingForm.postpaidSettings.creditRate) || 0)).toLocaleString()}
                                    </span>
                                  </div>
                                )}
                                
                                {/* SDK Fee - only shown when SDK is enabled */}
                                {sdkConfig?.enabled && (parseFloat(billingForm.postpaidSettings.sdkFee) || 0) > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-surface-300">
                                      <CodeBracketIcon className="w-3 h-3 inline mr-1" />
                                      SDK Fee
                                    </span>
                                    <span className="text-surface-200 font-medium">
                                      {billingForm.postpaidSettings.currencySymbol || 'Rp'} {(parseFloat(billingForm.postpaidSettings.sdkFee) || 0).toLocaleString()}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Divider */}
                                <div className="border-t border-surface-600 pt-2 mt-2">
                                  <div className="flex justify-between text-sm font-semibold">
                                    <span className="text-surface-200">Estimated Total</span>
                                    <span className="text-emerald-400 text-lg">
                                      {billingForm.postpaidSettings.currencySymbol || 'Rp'} {(
                                        (parseFloat(billingForm.postpaidSettings.licenseFee) || 0) +
                                        (Math.max(1, Math.ceil(company.currentUsage?.storageUsedGB || 0)) * (parseFloat(billingForm.postpaidSettings.storageCostPerGB) || 0)) +
                                        ((postpaidReport?.totalCredits || 0) * (parseFloat(billingForm.postpaidSettings.creditRate) || 0)) +
                                        (sdkConfig?.enabled ? (parseFloat(billingForm.postpaidSettings.sdkFee) || 0) : 0)
                                      ).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Usage by Feature */}
                              {postpaidReport?.byFeature && Object.keys(postpaidReport.byFeature).length > 0 && (
                                <div className="mt-4">
                                  <div className="text-xs text-surface-400 mb-2">Credit Usage by Feature:</div>
                                  <div className="space-y-1">
                                    {Object.entries(postpaidReport.byFeature).map(([feature, count]) => (
                                      <div key={feature} className="flex justify-between text-xs">
                                        <span className="text-surface-300">{feature}</span>
                                        <span className="text-surface-400">{count} credits</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4 border-t border-surface-700">
                  <button type="button" onClick={onClose} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading} className="btn-primary flex-1">
                    {loading ? 'Updating...' : 'Update Billing Plan'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {activeTab === 'credits' && (
            <div className="space-y-6">
              {/* Current Balance */}
              {loadingCredits ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : creditBalance ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ClockIcon className="w-5 h-5 text-purple-400" />
                      <span className="text-sm text-surface-400">Monthly Credits</span>
                    </div>
                    <p className="text-3xl font-bold text-surface-100">
                      {creditBalance.balance?.monthly?.available?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-surface-500 mt-1">
                      of {creditBalance.balance?.monthly?.total?.toLocaleString() || 0} (Resets monthly)
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl text-emerald-400">∞</span>
                      <span className="text-sm text-surface-400">Permanent Credits</span>
                    </div>
                    <p className="text-3xl font-bold text-surface-100">
                      {creditBalance.balance?.permanent?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-surface-500 mt-1">
                      Never expires
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center text-surface-400 py-8">
                  Failed to load credit balance
                </div>
              )}
              
              {/* Adjust Credits Form */}
              <div className="border-t border-surface-700 pt-6">
                <h4 className="text-sm font-medium text-surface-300 mb-4">Adjust Credits</h4>
                <form onSubmit={handleAdjustCredits} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Amount
                      </label>
                      <input
                        type="number"
                        value={creditForm.amount}
                        onChange={(e) => setCreditForm(p => ({ ...p, amount: e.target.value }))}
                        className="input"
                        placeholder="Enter amount (+ or -)"
                        required
                      />
                      <p className="text-xs text-surface-500 mt-1">
                        Use positive numbers to add, negative to deduct
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Credit Type
                      </label>
                      <select
                        value={creditForm.creditType}
                        onChange={(e) => setCreditForm(p => ({ ...p, creditType: e.target.value }))}
                        className="input"
                      >
                        <option value="permanent">Permanent (Non-expiring)</option>
                        <option value="monthly">Monthly (Expiring)</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Reason (required)
                    </label>
                    <textarea
                      value={creditForm.description}
                      onChange={(e) => setCreditForm(p => ({ ...p, description: e.target.value }))}
                      className="input"
                      rows="2"
                      placeholder="Note why credits are being adjusted..."
                      required
                    />
                  </div>
                  
                  <button type="submit" disabled={loading} className="btn-primary w-full">
                    {loading ? 'Adjusting...' : 'Adjust Credits'}
                  </button>
                </form>
              </div>
            </div>
          )}
          
          {activeTab === 'limits' && (
            <div className="space-y-6">
              <form onSubmit={handleUpdateLimits} className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-surface-300 mb-4">Seat Management</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Base Seats (from plan)
                      </label>
                      <input
                        type="number"
                        value={limitsForm.maxSeats}
                        onChange={(e) => setLimitsForm(p => ({ ...p, maxSeats: e.target.value }))}
                        className="input"
                        min="1"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Additional Seats
                        <span className="text-yellow-400 ml-1" title="These seats are billed monthly">💰</span>
                      </label>
                      <input
                        type="number"
                        value={limitsForm.additionalSeats}
                        onChange={(e) => setLimitsForm(p => ({ ...p, additionalSeats: e.target.value }))}
                        className="input"
                        min="0"
                      />
                      <p className="text-xs text-yellow-500/70 mt-1">
                        Counts towards billing
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Gifted Seats
                        <span className="text-green-400 ml-1" title="Free seats - not billed">🎁</span>
                      </label>
                      <input
                        type="number"
                        value={limitsForm.giftedSeats}
                        onChange={(e) => setLimitsForm(p => ({ ...p, giftedSeats: e.target.value }))}
                        className="input"
                        min="0"
                      />
                      <p className="text-xs text-green-500/70 mt-1">
                        Free - not billed
                      </p>
                    </div>
                  </div>
                  
                  {/* Seat Summary */}
                  <div className="mt-4 p-3 bg-surface-700/50 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-surface-400">Total Seat Limit:</span>
                      <span className="text-surface-200 font-medium">
                        {parseInt(limitsForm.maxSeats) + parseInt(limitsForm.additionalSeats || 0) + parseInt(limitsForm.giftedSeats || 0)} seats
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-surface-400">Billable Seats:</span>
                      <span className="text-yellow-400">
                        {parseInt(limitsForm.maxSeats) + parseInt(limitsForm.additionalSeats || 0)} seats
                      </span>
                    </div>
                    {parseInt(limitsForm.giftedSeats || 0) > 0 && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-surface-400">Free Seats:</span>
                        <span className="text-green-400">
                          {parseInt(limitsForm.giftedSeats || 0)} seats
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm mt-1 pt-2 border-t border-surface-600">
                      <span className="text-surface-400">Current Usage:</span>
                      <span className="text-surface-200">
                        {company.currentUsage?.seats || 0} / {parseInt(limitsForm.maxSeats) + parseInt(limitsForm.additionalSeats || 0) + parseInt(limitsForm.giftedSeats || 0)} seats
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-surface-700 pt-4">
                  <h4 className="text-sm font-medium text-surface-300 mb-4">Storage Management</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Base Storage Limit (from plan)
                      </label>
                      <input
                        type="number"
                        value={limitsForm.maxStorageGB}
                        onChange={(e) => setLimitsForm(p => ({ ...p, maxStorageGB: e.target.value }))}
                        className="input"
                        min="0.1"
                        step="0.1"
                        required
                      />
                      <p className="text-xs text-surface-500 mt-1">GB (e.g., 0.5 GB = 500 MB)</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-2">
                        Custom Storage (override)
                      </label>
                      <input
                        type="number"
                        value={limitsForm.customStorageGB}
                        onChange={(e) => setLimitsForm(p => ({ ...p, customStorageGB: e.target.value }))}
                        className="input"
                        min="0"
                        step="0.1"
                      />
                      <p className="text-xs text-surface-500 mt-1">
                        Total: {(parseFloat(limitsForm.maxStorageGB) + parseFloat(limitsForm.customStorageGB || 0)).toFixed(1)} GB
                      </p>
                    </div>
                  </div>
                </div>
                
                {company.billingPlan === 'enterprise' && (
                  <div className="border-t border-surface-700 pt-4">
                    <h4 className="text-sm font-medium text-surface-300 mb-4">Shared Dashboards</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-2">
                          Max Shared Dashboards
                        </label>
                        <input
                          type="number"
                          value={limitsForm.customSharedDashboards}
                          onChange={(e) => setLimitsForm(p => ({ ...p, customSharedDashboards: e.target.value }))}
                          className="input"
                          min="0"
                          placeholder="Unlimited if empty"
                        />
                        <p className="text-xs text-surface-500 mt-1">
                          Maximum number of dashboards this company can share publicly. Leave empty for unlimited.
                        </p>
                      </div>
                      <div className="flex items-end">
                        <div className="p-3 bg-surface-700/50 rounded-lg w-full">
                          <div className="flex justify-between text-sm">
                            <span className="text-surface-400">Current Usage:</span>
                            <span className="text-surface-200 font-medium">
                              {company.stats?.sharedDashboardCount ?? company.currentUsage?.sharedDashboards ?? 0} shared
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4 border-t border-surface-700">
                  <button type="button" onClick={onClose} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading} className="btn-primary flex-1">
                    {loading ? 'Updating...' : 'Update Limits'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {activeTab === 'costs' && (
            <div className="space-y-6">
              {/* Period Selector */}
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-surface-300">Cost Analysis</h4>
                <div className="flex items-center gap-2">
                  <select
                    value={costPeriod.month}
                    onChange={(e) => setCostPeriod(p => ({ ...p, month: parseInt(e.target.value) }))}
                    className="input py-1.5 text-sm"
                  >
                    {['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'
                    ].map((month, idx) => (
                      <option key={month} value={idx + 1}>{month}</option>
                    ))}
                  </select>
                  <select
                    value={costPeriod.year}
                    onChange={(e) => setCostPeriod(p => ({ ...p, year: parseInt(e.target.value) }))}
                    className="input py-1.5 text-sm"
                  >
                    {[2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {loadingCosts ? (
                <div className="flex items-center justify-center h-48">
                  <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : costAnalysis ? (
                <div className="space-y-6">
                  {/* Total Cost Summary */}
                  <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-lg p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-surface-400 mb-1">Total Monthly Cost (Your Expense)</p>
                        <p className="text-3xl font-bold text-surface-100">
                          ${costAnalysis.totalCosts.usd.toLocaleString()}
                        </p>
                        <p className="text-sm text-surface-500 mt-1">
                          ≈ Rp {costAnalysis.totalCosts.idr.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-surface-400 mb-2">Usage This Period</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between gap-4">
                            <span className="text-surface-400">Credits Used:</span>
                            <span className="text-surface-200 font-medium">{costAnalysis.usage.creditsUsed.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-surface-400">Users:</span>
                            <span className="text-surface-200 font-medium">{costAnalysis.usage.users}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-surface-400">Storage:</span>
                            <span className="text-surface-200 font-medium">{costAnalysis.usage.storageGB} GB</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Cost Breakdown Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* AI Token Costs */}
                    <div className="bg-surface-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CpuChipIcon className="w-5 h-5 text-purple-400" />
                        <span className="text-sm font-medium text-surface-300">AI Token Usage (Claude)</span>
                      </div>
                      <p className="text-2xl font-bold text-surface-100 mb-1">
                        ${costAnalysis.aiTokenUsage.costUSD.toLocaleString()}
                      </p>
                      <p className="text-xs text-surface-500 mb-3">
                        {costAnalysis.totalCosts.breakdown.aiTokens.percentage}% of total
                      </p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-surface-400">Credits Used:</span>
                          <span className="text-surface-300">{costAnalysis.aiTokenUsage.totalCreditsUsed.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Est. Input Tokens:</span>
                          <span className="text-surface-300">{costAnalysis.aiTokenUsage.estimatedInputTokens.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Est. Output Tokens:</span>
                          <span className="text-surface-300">{costAnalysis.aiTokenUsage.estimatedOutputTokens.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* BigQuery Costs */}
                    <div className="bg-surface-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CloudIcon className="w-5 h-5 text-blue-400" />
                        <span className="text-sm font-medium text-surface-300">BigQuery Processing</span>
                      </div>
                      <p className="text-2xl font-bold text-surface-100 mb-1">
                        ${costAnalysis.bigQueryCosts.totalCostUSD.toLocaleString()}
                      </p>
                      <p className="text-xs text-surface-500 mb-3">
                        {costAnalysis.totalCosts.breakdown.bigQuery.percentage}% of total
                      </p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-surface-400">Storage:</span>
                          <span className="text-surface-300">{costAnalysis.bigQueryCosts.storageGB} GB (${costAnalysis.bigQueryCosts.storageCostUSD})</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Est. Queries:</span>
                          <span className="text-surface-300">{costAnalysis.bigQueryCosts.estimatedQueries.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Query Cost:</span>
                          <span className="text-surface-300">${costAnalysis.bigQueryCosts.queryCostUSD}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Server Costs */}
                    <div className="bg-surface-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <ServerStackIcon className="w-5 h-5 text-emerald-400" />
                        <span className="text-sm font-medium text-surface-300">Server & Infrastructure</span>
                      </div>
                      <p className="text-2xl font-bold text-surface-100 mb-1">
                        ${costAnalysis.serverCosts.totalCostUSD.toLocaleString()}
                      </p>
                      <p className="text-xs text-surface-500 mb-3">
                        {costAnalysis.totalCosts.breakdown.server.percentage}% of total
                      </p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-surface-400">Base:</span>
                          <span className="text-surface-300">${costAnalysis.serverCosts.baseCost}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Users ({costAnalysis.usage.users}):</span>
                          <span className="text-surface-300">${costAnalysis.serverCosts.usersCost}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Data Sources ({costAnalysis.usage.dataSources}):</span>
                          <span className="text-surface-300">${costAnalysis.serverCosts.dataSourcesCost}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Database Costs */}
                    <div className="bg-surface-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CircleStackIcon className="w-5 h-5 text-amber-400" />
                        <span className="text-sm font-medium text-surface-300">Database (MongoDB)</span>
                      </div>
                      <p className="text-2xl font-bold text-surface-100 mb-1">
                        ${costAnalysis.databaseCosts.totalCostUSD.toLocaleString()}
                      </p>
                      <p className="text-xs text-surface-500 mb-3">
                        {costAnalysis.totalCosts.breakdown.database.percentage}% of total
                      </p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-surface-400">Base:</span>
                          <span className="text-surface-300">${costAnalysis.databaseCosts.baseCost}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Data Storage:</span>
                          <span className="text-surface-300">${costAnalysis.databaseCosts.dataCost}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Operations:</span>
                          <span className="text-surface-300">${(costAnalysis.databaseCosts.readsCost + costAnalysis.databaseCosts.writesCost).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* AI Usage by Feature */}
                  {costAnalysis.aiTokenUsage.usageByFeature.length > 0 && (
                    <div className="border-t border-surface-700 pt-4">
                      <h5 className="text-sm font-medium text-surface-300 mb-3">AI Usage by Feature</h5>
                      <div className="space-y-2">
                        {costAnalysis.aiTokenUsage.usageByFeature.map((feature, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-surface-700/30 rounded px-3 py-2">
                            <div>
                              <span className="text-sm text-surface-200">{feature.featureName}</span>
                              <span className="text-xs text-surface-500 ml-2">({feature.usageCount} uses)</span>
                            </div>
                            <span className="text-sm font-medium text-surface-300">
                              {feature.creditsUsed} credits
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Cost Rate Info */}
                  <div className="border-t border-surface-700 pt-4">
                    <p className="text-xs text-surface-500">
                      * Costs are estimated based on usage patterns and current cloud provider rates.
                      AI costs based on Claude Sonnet pricing (${costAnalysis.costRates?.aiCosts?.claudeSonnet?.inputPer1M}/1M input, ${costAnalysis.costRates?.aiCosts?.claudeSonnet?.outputPer1M}/1M output tokens).
                      BigQuery at ${costAnalysis.costRates?.bigquery?.queryPerTB}/TB queried.
                      Exchange rate: 1 USD = Rp {costAnalysis.costRates?.usdToIdr?.toLocaleString()}.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center text-surface-400 py-12">
                  <CurrencyDollarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No cost data available for this period</p>
                </div>
              )}
            </div>
          )}
          
          {/* SDK Tab Content */}
          {activeTab === 'sdk' && billingForm.billingPlan === 'enterprise' && (
            <div className="space-y-6">
              {loadingSDK ? (
                <div className="flex items-center justify-center h-48">
                  <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  {/* Section 1: SDK Status */}
                  <div className="bg-surface-700/30 border border-surface-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="text-sm font-medium text-surface-200 flex items-center gap-2">
                        <CodeBracketIcon className="w-5 h-5 text-primary-400" />
                        SDK Status
                      </h5>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <span className="text-sm text-surface-400">
                          {sdkForm.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={sdkForm.enabled}
                            onChange={(e) => setSdkForm(p => ({ ...p, enabled: e.target.checked }))}
                            className="sr-only"
                          />
                          <div className={`w-11 h-6 rounded-full transition-colors ${sdkForm.enabled ? 'bg-primary-500' : 'bg-surface-600'}`}>
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${sdkForm.enabled ? 'translate-x-5' : ''}`} />
                          </div>
                        </div>
                      </label>
                    </div>
                    
                    {/* API Key Section */}
                    {sdkForm.enabled && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs text-surface-400">API Key</span>
                            {sdkConfig?.hasApiKey ? (
                              <div className="flex items-center gap-2 mt-1">
                                <code className="text-sm text-surface-200 bg-surface-800 px-2 py-1 rounded">
                                  nsk_••••••••••••{sdkConfig.apiKeyLast4}
                                </code>
                                <span className="text-xs text-surface-500">
                                  Created {new Date(sdkConfig.apiKeyCreatedAt).toLocaleDateString()}
                                </span>
                              </div>
                            ) : (
                              <p className="text-sm text-amber-400 mt-1">No API key generated yet</p>
                            )}
                          </div>
                          <button
                            onClick={handleRegenerateApiKey}
                            disabled={loading}
                            className="btn-secondary btn-sm"
                          >
                            <KeyIcon className="w-4 h-4 mr-1" />
                            {sdkConfig?.hasApiKey ? 'Regenerate' : 'Generate Key'}
                          </button>
                        </div>
                        
                        {/* Show new API key if just generated */}
                        {newApiKey && (
                          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                            <p className="text-xs text-emerald-400 mb-2 font-medium">
                              New API Key (copy now - won't be shown again!)
                            </p>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-sm text-emerald-300 bg-surface-800 px-3 py-2 rounded font-mono break-all">
                                {showApiKey ? newApiKey : '•'.repeat(40)}
                              </code>
                              <button
                                onClick={() => setShowApiKey(!showApiKey)}
                                className="p-2 text-surface-400 hover:text-surface-200"
                                title={showApiKey ? 'Hide' : 'Show'}
                              >
                                {showApiKey ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => copyToClipboard(newApiKey)}
                                className="p-2 text-surface-400 hover:text-surface-200"
                                title="Copy"
                              >
                                <ClipboardDocumentIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Usage Stats */}
                        {sdkConfig && (
                          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-surface-700">
                            <div className="text-center">
                              <p className="text-lg font-semibold text-surface-100">{sdkConfig.totalRequests?.toLocaleString() || 0}</p>
                              <p className="text-xs text-surface-500">Total Requests</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-semibold text-surface-100">{sdkConfig.totalCreditsUsed?.toLocaleString() || 0}</p>
                              <p className="text-xs text-surface-500">Credits Used</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-semibold text-surface-100">
                                {sdkConfig.lastRequestAt ? new Date(sdkConfig.lastRequestAt).toLocaleDateString() : '-'}
                              </p>
                              <p className="text-xs text-surface-500">Last Request</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Section 2: Filter Fields Configuration */}
                  {sdkForm.enabled && (
                    <div className="bg-surface-700/30 border border-surface-700 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-surface-200 mb-4">Filter Fields</h5>
                      <p className="text-xs text-surface-400 mb-4">
                        Define filter fields that SDK clients must provide. These filters restrict the data the AI can access.
                      </p>
                      
                      {/* Filter List */}
                      {sdkForm.allowedFilters.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {sdkForm.allowedFilters.map((filter, index) => (
                            <div key={index} className="flex items-center gap-3 bg-surface-800/50 rounded-lg p-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <code className="text-sm text-primary-400">{filter.fieldName}</code>
                                  <span className="text-xs text-surface-500">({filter.dataType})</span>
                                  {filter.required && (
                                    <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Required</span>
                                  )}
                                </div>
                                <p className="text-xs text-surface-400 mt-1">{filter.displayName}</p>
                                {filter.description && (
                                  <p className="text-xs text-surface-500 mt-1 italic">{filter.description}</p>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleEditFilter(index)}
                                  className="p-1.5 text-surface-400 hover:text-primary-400"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteFilter(index)}
                                  className="p-1.5 text-surface-400 hover:text-red-400"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add/Edit Filter Form */}
                      <div className="border-t border-surface-700 pt-4">
                        <h6 className="text-xs font-medium text-surface-300 mb-3">
                          {editingFilter !== null ? 'Edit Filter' : 'Add Filter'}
                        </h6>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-surface-400 mb-1">Field Name (variable)</label>
                            <input
                              type="text"
                              value={filterForm.fieldName}
                              onChange={(e) => setFilterForm(p => ({ ...p, fieldName: e.target.value }))}
                              placeholder="companyId"
                              className="input text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-surface-400 mb-1">Display Name</label>
                            <input
                              type="text"
                              value={filterForm.displayName}
                              onChange={(e) => setFilterForm(p => ({ ...p, displayName: e.target.value }))}
                              placeholder="Company ID"
                              className="input text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-surface-400 mb-1">Description (for AI context)</label>
                            <textarea
                              value={filterForm.description}
                              onChange={(e) => setFilterForm(p => ({ ...p, description: e.target.value }))}
                              placeholder="This is the company's unique identifier. Only show data for this company."
                              className="input text-sm"
                              rows={2}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-surface-400 mb-1">Data Type</label>
                            <select
                              value={filterForm.dataType}
                              onChange={(e) => setFilterForm(p => ({ ...p, dataType: e.target.value }))}
                              className="input text-sm"
                            >
                              <option value="string">String</option>
                              <option value="number">Number</option>
                              <option value="boolean">Boolean</option>
                            </select>
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filterForm.required}
                                onChange={(e) => setFilterForm(p => ({ ...p, required: e.target.checked }))}
                                className="rounded border-surface-600 text-primary-500"
                              />
                              <span className="text-sm text-surface-300">Required</span>
                            </label>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-3">
                          {editingFilter !== null && (
                            <button
                              onClick={() => {
                                setEditingFilter(null);
                                setFilterForm({ fieldName: '', displayName: '', description: '', dataType: 'string', required: false });
                              }}
                              className="btn-ghost btn-sm"
                            >
                              Cancel
                            </button>
                          )}
                          <button
                            onClick={editingFilter !== null ? handleUpdateFilter : handleAddFilter}
                            className="btn-primary btn-sm"
                          >
                            {editingFilter !== null ? 'Update Filter' : 'Add Filter'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Section 3: Custom Prompt Editor */}
                  {sdkForm.enabled && (
                    <div className="bg-surface-700/30 border border-surface-700 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-surface-200 mb-2">Custom Prompt</h5>
                      <p className="text-xs text-surface-400 mb-3">
                        Define a custom system prompt for the AI. Use {'{{fieldName}}'} to insert filter values.
                      </p>
                      
                      {/* Variable Insert Buttons */}
                      {sdkForm.allowedFilters.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          <span className="text-xs text-surface-500">Insert variable:</span>
                          {sdkForm.allowedFilters.map((filter) => (
                            <button
                              key={filter.fieldName}
                              onClick={() => setSdkForm(p => ({
                                ...p,
                                customPrompt: p.customPrompt + `{{${filter.fieldName}}}`
                              }))}
                              className="text-xs px-2 py-0.5 bg-surface-700 text-primary-400 rounded hover:bg-surface-600"
                            >
                              {`{{${filter.fieldName}}}`}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <textarea
                        value={sdkForm.customPrompt}
                        onChange={(e) => setSdkForm(p => ({ ...p, customPrompt: e.target.value }))}
                        placeholder={`Example:\nWe are working with a multi-tenant data set. The current user belongs to company "{{companyName}}" (ID: {{companyId}}).\n\nIMPORTANT RULES:\n- Only access data belonging to {{companyName}}\n- Never mention other companies' data`}
                        className="input text-sm font-mono"
                        rows={8}
                      />
                    </div>
                  )}
                  
                  {/* Section 4: Widget Customization */}
                  {sdkForm.enabled && (
                    <div className="bg-surface-700/30 border border-surface-700 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-surface-200 mb-4">Widget Customization</h5>
                      
                      {/* Display Mode Selection */}
                      <div className="mb-4">
                        <label className="block text-xs text-surface-400 mb-2">Display Mode</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setSdkForm(p => ({
                              ...p,
                              widgetConfig: { ...p.widgetConfig, displayMode: 'floating' }
                            }))}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              (sdkForm.widgetConfig?.displayMode || 'floating') === 'floating'
                                ? 'border-primary-500 bg-primary-500/10'
                                : 'border-surface-600 hover:border-surface-500'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-surface-600 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-surface-200">Floating Button</p>
                                <p className="text-xs text-surface-400">Chat opens from a button</p>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSdkForm(p => ({
                              ...p,
                              widgetConfig: { ...p.widgetConfig, displayMode: 'embedded' }
                            }))}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              sdkForm.widgetConfig?.displayMode === 'embedded'
                                ? 'border-primary-500 bg-primary-500/10'
                                : 'border-surface-600 hover:border-surface-500'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-surface-600 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                </svg>
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-surface-200">Embedded</p>
                                <p className="text-xs text-surface-400">Chat always visible inline</p>
                              </div>
                            </div>
                          </button>
                        </div>
                        <p className="text-xs text-surface-500 mt-2">
                          {(sdkForm.widgetConfig?.displayMode || 'floating') === 'floating' 
                            ? 'A floating chat button will appear in the corner. Users click to open the chat window.'
                            : 'The chat interface will be embedded directly into a container element on your page.'}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-surface-400 mb-1">Primary Color</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={sdkForm.widgetConfig?.primaryColor || '#6366f1'}
                              onChange={(e) => setSdkForm(p => ({
                                ...p,
                                widgetConfig: { ...p.widgetConfig, primaryColor: e.target.value }
                              }))}
                              className="w-10 h-10 rounded cursor-pointer border border-surface-600"
                            />
                            <input
                              type="text"
                              value={sdkForm.widgetConfig?.primaryColor || '#6366f1'}
                              onChange={(e) => setSdkForm(p => ({
                                ...p,
                                widgetConfig: { ...p.widgetConfig, primaryColor: e.target.value }
                              }))}
                              className="input text-sm flex-1"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-surface-400 mb-1">Widget Title</label>
                          <input
                            type="text"
                            value={sdkForm.widgetConfig?.title || ''}
                            onChange={(e) => setSdkForm(p => ({
                              ...p,
                              widgetConfig: { ...p.widgetConfig, title: e.target.value }
                            }))}
                            className="input text-sm"
                            placeholder="AI Assistant"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-surface-400 mb-1">Input Placeholder</label>
                          <input
                            type="text"
                            value={sdkForm.widgetConfig?.placeholder || ''}
                            onChange={(e) => setSdkForm(p => ({
                              ...p,
                              widgetConfig: { ...p.widgetConfig, placeholder: e.target.value }
                            }))}
                            className="input text-sm"
                            placeholder="Ask me anything..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-surface-400 mb-1">Welcome Message</label>
                          <input
                            type="text"
                            value={sdkForm.widgetConfig?.welcomeMessage || ''}
                            onChange={(e) => setSdkForm(p => ({
                              ...p,
                              widgetConfig: { ...p.widgetConfig, welcomeMessage: e.target.value }
                            }))}
                            className="input text-sm"
                            placeholder="Hello! How can I help you today?"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Save Button */}
                  {sdkForm.enabled && (
                    <div className="flex justify-end gap-3">
                      <button onClick={onClose} className="btn-secondary">
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateSDKConfig}
                        disabled={loading}
                        className="btn-primary"
                      >
                        {loading ? 'Saving...' : 'Save SDK Configuration'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function CompanyCard({ company, onToggleStatus, onViewDetails }) {
  return (
    <div className="card hover:border-surface-600 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center">
            <BuildingOfficeIcon className="w-6 h-6 text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-surface-100">{company.name}</h3>
            <p className="text-sm text-surface-400">{company.industry || 'No industry set'}</p>
          </div>
        </div>
        <span className={company.isActive ? 'badge-success' : 'badge-danger'}>
          {company.isActive ? (
            <><CheckCircleIcon className="w-3 h-3 mr-1" /> Active</>
          ) : (
            <><XCircleIcon className="w-3 h-3 mr-1" /> Inactive</>
          )}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-surface-700/50 rounded-lg p-3 text-center">
          <UsersIcon className="w-5 h-5 text-surface-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-surface-100">{company.userCount || 0}</p>
          <p className="text-xs text-surface-400">Users</p>
        </div>
        <div className="bg-surface-700/50 rounded-lg p-3 text-center">
          <ChartBarSquareIcon className="w-5 h-5 text-surface-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-surface-100">{company.dashboardCount || 0}</p>
          <p className="text-xs text-surface-400">Dashboards</p>
        </div>
        <div className="bg-surface-700/50 rounded-lg p-3 text-center">
          <CircleStackIcon className="w-5 h-5 text-surface-400 mx-auto mb-1" />
          <p className="text-lg font-semibold text-surface-100">
            {company.currentUsage?.storageUsedGB?.toFixed(1) || 0}
          </p>
          <p className="text-xs text-surface-400">GB Used</p>
        </div>
      </div>
      
      {/* Limits */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-surface-400">
            Seat Limit
            {(company.giftedSeats || 0) > 0 && (
              <span className="text-green-400 ml-1" title={`${company.giftedSeats} gifted seats`}>🎁</span>
            )}
          </span>
          <span className="text-surface-300">
            {company.currentUsage?.seats || 0} / {(company.billingLimits?.maxSeats || 1) + (company.additionalSeats || 0) + (company.giftedSeats || 0)}
          </span>
        </div>
        <div className="w-full h-1.5 bg-surface-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary-500 rounded-full"
            style={{ 
              width: `${Math.min(100, ((company.currentUsage?.seats || 0) / ((company.billingLimits?.maxSeats || 1) + (company.additionalSeats || 0) + (company.giftedSeats || 0))) * 100)}%`
            }}
          />
        </div>
      </div>
      
      <div className="flex gap-2 pt-4 border-t border-surface-700">
        <button
          onClick={() => onToggleStatus(company)}
          className={company.isActive ? 'btn-ghost btn-sm text-red-400 flex-1' : 'btn-ghost btn-sm text-emerald-400 flex-1'}
        >
          {company.isActive ? 'Deactivate' : 'Activate'}
        </button>
        <button 
          onClick={() => onViewDetails(company)}
          className="btn-secondary btn-sm flex-1"
        >
          View Details
        </button>
      </div>
    </div>
  );
}

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  useEffect(() => {
    loadCompanies();
  }, [pagination.page, filters]);
  
  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getCompanies({
        page: pagination.page,
        limit: 12,
        ...filters
      });
      setCompanies(response.data.companies);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleCompanyStatus = async (company) => {
    try {
      await adminApi.updateCompany(company._id, { isActive: !company.isActive });
      toast.success(`Company ${company.isActive ? 'deactivated' : 'activated'}`);
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update company');
    }
  };
  
  const handleViewDetails = (company) => {
    setSelectedCompany(company);
    setShowDetailsModal(true);
  };
  
  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCompany(null);
  };
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Company Management</h1>
          <p className="text-surface-400 mt-1">Manage all companies on the platform</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <PlusIcon className="w-5 h-5" />
          Add Company
        </button>
      </div>
      
      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              type="text"
              placeholder="Search companies..."
              value={filters.search}
              onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
              className="input pl-10"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
            className="input"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      
      {/* Companies Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : companies.length === 0 ? (
        <div className="card text-center py-12">
          <BuildingOfficeIcon className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-300 mb-2">No companies found</h3>
          <p className="text-surface-400 mb-6">Get started by creating your first company</p>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <PlusIcon className="w-5 h-5" />
            Add Company
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map(company => (
            <CompanyCard 
              key={company._id} 
              company={company}
              onToggleStatus={toggleCompanyStatus}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-surface-400 text-sm">
            Showing {companies.length} of {pagination.total} companies
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      <CreateCompanyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadCompanies}
      />
      
      <CompanyDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
        company={selectedCompany}
        onSuccess={loadCompanies}
      />
    </div>
  );
}

